// The River: a horizontal lineup of songs flowing past a fixed playhead. Only the cards around
// the playhead are rendered (virtualised); the track moves with one transform per frame.
// Everything sits on one time scale: a card is exactly its song (16 bars), the gap after it is the
// break bar, and one continuous band of notes runs through all of them — the songs' notes and the
// breaks' drum hits, which flash as the playhead crosses them.
import { useMyPlays } from "../myPlays";
import { useEffect, useMemo, useRef, useState } from "react";
import { keyShort, SLOT_BARS, SONG_BARS, songKey, TICKS_PER_BAR, wizardLabel, type PeriodDef } from "@eb/shared";
import { clock } from "../stream";
import { t } from "../i18n";
import { LikeButton } from "../ui/Like";
import { AddToTapeButton } from "../tapes/AddToTape";
import { prefersCalm } from "../ui/settings";
import { EncoreMeter } from "./EncoreMeter";
import { MiniNotes } from "./MiniNotes";
import type { RiverPlayer } from "./player";
import { slotAt, slotsBetween, type SlotRef } from "./slots";
import { useSlotData } from "./useSlotData";

const { SLOT_MS, MS_PER_BAR } = clock;
const SONG_MS = MS_PER_BAR * SONG_BARS;

const BEFORE = 3;
const AFTER = 5;

export function RiverLane({
  player,
  period,
  onOpen,
}: {
  player: RiverPlayer;
  period: PeriodDef | null;
  onOpen: (s: SlotRef) => void;
}) {
  const epoch = player.epochMs();
  const trackRef = useRef<HTMLDivElement>(null);
  const laneRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(() => slotAt(player.worldNow(), epoch));
  // Width of one slot (a song and its break bar).
  const [slotW, setSlotW] = useState(420);
  const refMs = useMemo(() => current.startMs, [current.day]); // eslint-disable-line react-hooks/exhaustive-deps
  const pxPerMs = slotW / SLOT_MS;
  const songW = (slotW * SONG_BARS) / SLOT_BARS;
  const breakW = slotW / SLOT_BARS;
  // The band's height (the cards' middle: shorter on phones).
  const bandH = slotW < 300 ? 48 : 74;
  // The break of the song playing now has started (its drum hits flash as they pass).
  const [inBreak, setInBreak] = useState(false);

  useEffect(() => {
    const lane = laneRef.current;
    if (!lane) return;
    const ro = new ResizeObserver(() => setSlotW(lane.clientWidth < 640 ? 260 : 420));
    ro.observe(lane);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let raf = 0;
    let lastStep = -1;
    const frame = () => {
      const now = player.worldNow();
      const lane = laneRef.current;
      const track = trackRef.current;
      if (lane && track) {
        let x = (now - refMs) * pxPerMs;
        // Calm motion: move in whole-bar steps instead of a continuous glide.
        if (prefersCalm()) {
          const step = Math.floor(now / clock.MS_PER_BAR);
          if (step === lastStep) {
            raf = requestAnimationFrame(frame);
            return;
          }
          lastStep = step;
          x = (step * clock.MS_PER_BAR - refMs) * pxPerMs;
        }
        track.style.transform = `translate3d(${(lane.clientWidth / 2 - x).toFixed(1)}px,0,0)`;
      }
      const s = slotAt(now, epoch);
      if (s.key !== current.key) setCurrent(s);
      const brk = now - s.startMs >= SONG_MS;
      setInBreak((b) => (b === brk ? b : brk));
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [player, epoch, refMs, pxPerMs, current.key]);

  const visible = useMemo(
    () => slotsBetween(current.startMs - BEFORE * SLOT_MS, current.startMs + (AFTER + 1) * SLOT_MS, epoch),
    [current.startMs, epoch],
  );
  const data = useSlotData(player, visible);
  const myIds = useMyPlays((s) => s.loopIds);

  return (
    <div className="river-lane" ref={laneRef} aria-hidden="false">
      <div className="river-track" ref={trackRef}>
        {visible.map((s) => {
          const d = data.get(s.key);
          const e = d?.entry;
          const isNow = s.key === current.key;
          const past = s.startMs + SLOT_MS <= current.startMs;
          const mark = e ? wizardLabel(e) : null;
          const author = e ? (e.author ?? t("river.formerContributor")) : "…";
          // A heart on human songs that have started (songs can be liked once they've played).
          const likeable = e?.kind === "human" && !!e.author && s.startMs <= player.worldNow();
          const mine = !!e && e.kind === "human" && myIds.has(e.loopId);
          const left = (s.startMs - refMs) * pxPerMs;
          return [
            <button
              type="button"
              key={s.key}
              className={`river-card${isNow ? " now" : ""}${past ? " past" : ""}${e?.kind === "filler" ? " filler" : ""}${likeable ? " has-like" : ""}${mine ? " mine" : ""}`}
              style={{ left, width: songW }}
              onClick={() => onOpen(s)}
              aria-label={`${isNow ? `${t("river.nowPlaying")}: ` : ""}${mine ? "your " : ""}song by ${author}${e?.repeat ? `, encore ${e.repeat + 1}` : ""}${mark ? `, ${mark}` : ""}`}
              data-testid={isNow ? "river-card-now" : undefined}
              data-slot={`${s.day}:${s.slot}`}
            >
              <span className="card-top">
                <span className="card-author">{author}</span>
                {mine && <span className="chip mine-chip">Your song</span>}
                {mark && <span className="wizard-badge">✨ {mark}</span>}
              </span>
              <span className="card-band-space" aria-hidden="true" />
              {isNow && e?.kind === "human" ? (
                <span className="card-meta">
                  <EncoreMeter day={s.day} slot={s.slot} loopId={e.loopId} />
                </span>
              ) : (
                <span className="card-meta">
                  {e?.repeat ? (
                    <span className="chip encore-chip">↻ Encore {e.repeat + 1}</span>
                  ) : (
                    // The song's key (the day's own or its relative); the day's kit is the same
                    // on every card, so it isn't repeated.
                    period && <span className="chip">{keyShort(songKey(period.key, d?.loop))}</span>
                  )}
                  <span className="card-slot">#{s.slot + 1}</span>
                </span>
              )}
            </button>,
            // A sibling, not a child: a button can't contain a button.
            // Live: put the song playing now on a tape.
            isNow && e && (
              <AddToTapeButton
                key={`${s.key}-tape`}
                loopId={e.loopId}
                className={`like-btn lane-like lane-tape now`}
                label="+"
                style={{ left: left + songW - (likeable ? 84 : 44) }}
              />
            ),
            likeable && (
              <LikeButton
                key={`${s.key}-like`}
                loopId={e.loopId}
                author={author}
                className={`lane-like${past ? " past" : ""}${isNow ? " now" : ""}`}
                style={{ left: left + songW - 44 }}
              />
            ),
          ];
        })}
        {/* One band of notes through every song and break (above the cards, never in the way). */}
        <div
          className="river-band"
          aria-hidden="true"
          style={{
            left: ((visible[0]?.startMs ?? refMs) - refMs) * pxPerMs,
            width: visible.length * slotW,
          }}
        />
        {visible.map((s) => {
          const d = data.get(s.key);
          const left = (s.startMs - refMs) * pxPerMs;
          const past = s.startMs + SLOT_MS <= current.startMs;
          const live = s.key === current.key && inBreak;
          return [
            <MiniNotes
              key={`${s.key}-notes`}
              loop={d?.loop}
              width={songW}
              height={bandH}
              className={`band-notes${past ? " past" : ""}`}
              style={{ left }}
              label={`Notes of the song by ${d?.entry?.author ?? "the Wizard"}`}
            />,
            period && (
              <BreakNotes
                key={`${s.key}-break`}
                def={period}
                left={left + songW}
                width={breakW}
                height={bandH}
                live={live}
                past={past}
              />
            ),
          ];
        })}
      </div>
      <div className="playhead" aria-hidden="true" />
      <div className="lane-fade left" aria-hidden="true" />
      <div className="lane-fade right" aria-hidden="true" />
    </div>
  );
}

/** A break bar's drum hits in the band (kick low, snares mid, hats high). While it plays they
 *  light up one by one as the playhead reaches them. */
function BreakNotes({
  def,
  left,
  width,
  height,
  live,
  past,
}: {
  def: PeriodDef;
  left: number;
  width: number;
  height: number;
  live: boolean;
  past: boolean;
}) {
  const hits = def.breakEvents ?? [];
  return (
    <span
      className={`band-break${live ? " live" : ""}${past ? " past" : ""}`}
      style={{ left, width }}
      aria-hidden="true"
    >
      {hits.map((e, i) => {
        const row = e.pad % 4;
        return (
          <i
            key={i}
            style={
              {
                left: (e.tick / TICKS_PER_BAR) * width,
                bottom: 4 + row * ((height - 12) / 4),
                opacity: 0.35 + (e.velocity / 127) * 0.65,
                "--d": `${Math.round((e.tick / TICKS_PER_BAR) * MS_PER_BAR)}ms`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </span>
  );
}
